import { useCallback, useRef } from 'react'

interface UseLongPressOptions {
  onLongPress: (e: React.TouchEvent | React.MouseEvent) => void
  onClick?: (e: React.TouchEvent | React.MouseEvent) => void
  ms?: number
  moveThreshold?: number // Distance in pixels before cancelling the long press
}

export function useLongPress(options: UseLongPressOptions) {
  const { onLongPress, onClick, ms = 500, moveThreshold = 10 } = options
  const timerRef = useRef<NodeJS.Timeout | undefined>(undefined)
  const isLongPress = useRef(false)
  const startPosRef = useRef<{ x: number; y: number } | null>(null)
  const hasMovedRef = useRef(false)

  const getPosition = (e: React.TouchEvent | React.MouseEvent) => {
    if ('touches' in e) {
      return {
        x: e.touches[0]?.clientX || 0,
        y: e.touches[0]?.clientY || 0
      }
    } else {
      return {
        x: (e as React.MouseEvent).clientX,
        y: (e as React.MouseEvent).clientY
      }
    }
  }

  const start = useCallback(
    (e: React.TouchEvent | React.MouseEvent) => {
      isLongPress.current = false
      hasMovedRef.current = false
      startPosRef.current = getPosition(e)
      timerRef.current = setTimeout(() => {
        if (!hasMovedRef.current) {
          isLongPress.current = true
          onLongPress(e)
        }
      }, ms)
    },
    [onLongPress, ms]
  )

  const handleMove = useCallback(
    (e: React.TouchEvent | React.MouseEvent) => {
      if (!startPosRef.current || hasMovedRef.current) return

      const currentPos = getPosition(e)
      const distance = Math.sqrt(
        Math.pow(currentPos.x - startPosRef.current.x, 2) +
        Math.pow(currentPos.y - startPosRef.current.y, 2)
      )

      // If user moved more than threshold, cancel the long press
      if (distance > moveThreshold) {
        hasMovedRef.current = true
        if (timerRef.current) {
          clearTimeout(timerRef.current)
        }
      }
    },
    [moveThreshold]
  )

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }
    startPosRef.current = null
    hasMovedRef.current = false
  }, [])

  const handleClick = useCallback(
    (e: React.TouchEvent | React.MouseEvent) => {
      if (!isLongPress.current && onClick) {
        onClick(e)
      }
      isLongPress.current = false
    },
    [onClick]
  )

  return {
    onMouseDown: start,
    onMouseUp: stop,
    onMouseLeave: stop,
    onMouseMove: handleMove,
    onTouchStart: start,
    onTouchEnd: stop,
    onTouchMove: handleMove,
    onClick: handleClick,
  }
}

